/**
 * ADD RESPONSIVE DISPLAY ADS — /google-ads/awareness/create/ad[?adgroup_id=…]
 *
 * One existing Display ad group, many ad drafts with the live multi-format
 * preview. The group's targeting is shown so the copy can be written for that
 * audience; new ads start from the group's existing brand (landing page,
 * business name, logo assets — reused, never re-uploaded). Resumable.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Plus } from 'lucide-react';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { Select } from '@/components/Select';
import { DisplayTargetingSummary, googleAdsService, GoogleAdsAdGroupRow } from '@/services/google-ads.service';
import { BackTo, errMsg, Section, Spinner, Tag, useYoutubeAuth } from '@/components/google-ads/youtube/youtubeShared';
import { AGE_LABELS, GENDER_LABELS } from '@/pages/google-ads/googleAdsTargeting';
import { ImageDraft, Pub, uid } from '@/components/google-ads/objectives/objectiveShared';
import { ChipList, ConnectFirst, GoalStrip, PageSpinner, SaveBar } from '@/components/google-ads/objectives/wizardChrome';
import {
  brandOf, cloneRda, newRda, publishRda, RdaBrand, RdaDraft, RdaDraftCard, rdaIssues, RdaPreview, writeRdaAsset,
} from '@/components/google-ads/display/displayShared';
import { AWARENESS_OBJECTIVE as CFG } from './awarenessObjective';

type Patch<T> = Partial<T> | ((cur: T) => Partial<T>);
const filled = (xs: string[]) => xs.map((x) => x.trim()).filter(Boolean);

/** Targeting summary → review chips. */
function targetingChips(t: DisplayTargetingSummary | null): string[] {
  if (!t) return [];
  const out: string[] = [];
  if (t.ages) out.push(t.ages.map((a) => AGE_LABELS[a] ?? a).join(', '));
  if (t.genders) out.push(t.genders.map((g) => GENDER_LABELS[g] ?? g).join(' / '));
  t.topics.forEach((x) => out.push(x.name));
  t.keywords.forEach((k) => out.push(`“${k}”`));
  t.placements.forEach((p) => out.push(p));
  if (t.youtubeChannels.length) out.push(`${t.youtubeChannels.length} YouTube channel${t.youtubeChannels.length === 1 ? '' : 's'}`);
  if (t.youtubeVideos.length) out.push(`${t.youtubeVideos.length} YouTube video${t.youtubeVideos.length === 1 ? '' : 's'}`);
  return out;
}

export default function CreateAwarenessAd() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { auth, accountUid } = useYoutubeAuth();
  const ready = !!(auth?.connected && auth?.customer_selected && accountUid);

  const [adgroups, setAdgroups] = useState<GoogleAdsAdGroupRow[] | null>(null);
  const [adgroupId, setAdgroupId] = useState(params.get('adgroup_id') || '');
  const [targeting, setTargeting] = useState<DisplayTargetingSummary | null>(null);
  const [loadingGroup, setLoadingGroup] = useState(false);
  const [existingAds, setExistingAds] = useState<number | null>(null);
  const [brand, setBrand] = useState<Partial<RdaBrand> | undefined>(undefined);
  const adgroup = adgroups?.find((g) => g.id === adgroupId);

  const first = useMemo(() => newRda(1), []);
  const [ads, setAds] = useState<RdaDraft[]>([first]);
  const [activeAd, setActiveAd] = useState(first.id);
  const patchAd = (aid: string, p: Patch<RdaDraft>) =>
    setAds((as) => as.map((a) => (a.id === aid ? { ...a, ...(typeof p === 'function' ? p(a) : p) } : a)));

  const [pub, setPub] = useState<Record<string, Pub>>({});
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const madeRef = useRef<Record<string, string>>({});

  useEffect(() => {
    if (!accountUid) return;
    googleAdsService.listAdGroups(accountUid)
      .then((l) => setAdgroups(l.filter((g) => (!g.campaignChannelType || g.campaignChannelType === 'DISPLAY') && g.status !== 'REMOVED')))
      .catch(() => setAdgroups([]));
  }, [accountUid]);

  useEffect(() => {
    setTargeting(null); setExistingAds(null); setBrand(undefined);
    if (!accountUid || !adgroupId) return;
    let alive = true;
    setLoadingGroup(true);
    Promise.all([
      googleAdsService.display.adGroupTargeting(accountUid, adgroupId).catch(() => null),
      googleAdsService.display.listAds(accountUid, { adGroupId: adgroupId }).catch(() => []),
    ]).then(([t, existing]) => {
      if (!alive) return;
      setTargeting(t);
      setExistingAds(existing.length);
      const ad = existing[0];
      if (!ad) return;
      const asDraft = (x: { resourceName: string; url?: string; width?: number; height?: number; name?: string }): ImageDraft =>
        ({ id: uid(), url: x.url ?? '', note: x.name ?? 'logo', width: x.width, height: x.height, resourceName: x.resourceName });
      const b: Partial<RdaBrand> = {
        finalUrl: ad.finalUrls[0] ?? '', businessName: ad.businessName ?? '', cta: ad.callToActionText ?? '',
        squareLogos: ad.squareLogos.filter((l) => l.url).map(asDraft), wideLogos: ad.logos.filter((l) => l.url).map(asDraft),
      };
      setBrand(b);
      setAds((cur) => cur.map((a) => ({
        ...a,
        finalUrl: a.finalUrl || b.finalUrl || '', businessName: a.businessName || b.businessName || '',
        squareLogos: a.squareLogos.length ? a.squareLogos : (b.squareLogos ?? []).map((i) => ({ ...i })),
        wideLogos: a.wideLogos.length ? a.wideLogos : (b.wideLogos ?? []).map((i) => ({ ...i })),
      })));
    }).finally(() => { if (alive) setLoadingGroup(false); });
    return () => { alive = false; };
  }, [accountUid, adgroupId]);

  const byCampaign = useMemo(() => {
    const m = new Map<string, { name: string; groups: GoogleAdsAdGroupRow[] }>();
    for (const g of adgroups ?? []) {
      const row = m.get(g.campaignId) ?? { name: g.campaignName || `Campaign ${g.campaignId}`, groups: [] };
      row.groups.push(g);
      m.set(g.campaignId, row);
    }
    return Array.from(m.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [adgroups]);

  const issues = useMemo(() => {
    const i: string[] = [];
    if (!adgroupId) i.push('Pick the ad group these ads belong to.');
    ads.forEach((a, k) => { const ai = rdaIssues(a); if (ai.length) i.push(`${a.name.trim() || `Ad ${k + 1}`}: needs ${ai.join(', ')}.`); });
    return i;
  }, [adgroupId, ads]);

  const submit = async () => {
    if (!accountUid || issues.length) return;
    setSaving(true); setError(null);
    const mark = (k: string, v: Pub) => setPub((m) => ({ ...m, [k]: v }));
    try {
      for (const a of ads) {
        if (madeRef.current[a.id]) continue;
        mark(a.id, 'running');
        try {
          const r = await publishRda(accountUid, adgroupId, a, (field, id, rn) => patchAd(a.id, writeRdaAsset(field, id, rn)));
          madeRef.current[a.id] = r.resourceName;
        } catch (e) { mark(a.id, 'failed'); setError(`${a.name.trim() || filled(a.headlines)[0] || 'Ad'}: ${errMsg(e)}`); return; }
        mark(a.id, 'done');
      }
      toast.success(`${ads.length === 1 ? 'Ad' : `${ads.length} ads`} created — Google reviews every ad; it stays paused until you enable it.`);
      navigate(CFG.base);
    } finally { setSaving(false); }
  };

  const active = ads.find((a) => a.id === activeAd) ?? ads[0] ?? null;

  return (
    <>
      <Topbar title="New display ads" breadcrumb={`Google Ads / ${CFG.title}`}
        description="Text, images and logos Google assembles into every banner size"
        back={<BackTo to={CFG.base} label={`Back to ${CFG.title}`} />} />

      <div className="w-full px-7 py-7">
        {auth === undefined && <PageSpinner />}
        {auth !== undefined && !ready && <ConnectFirst what="ads" />}

        {ready && (
          <div className="flex w-full gap-5 pb-12">
            <div className="min-w-0 flex-1 space-y-4">
              <GoalStrip items={[
                { label: 'Goal', value: CFG.goal },
                { label: 'Type', value: 'Responsive display ad' },
                { label: 'Adding to', value: adgroup ? `${adgroup.name}${adgroup.campaignName ? ` · ${adgroup.campaignName}` : ''}` : 'pick an ad group' },
              ]}>
                <Tag>starts paused</Tag>
              </GoalStrip>

              <Section title="Ad group" hint="The ads show to this group's audience.">
                <Select className="h-10 rounded-lg" value={adgroupId} onChange={(e) => setAdgroupId(e.target.value)} disabled={Object.keys(madeRef.current).length > 0}>
                  <option value="">{adgroups === null ? 'Loading ad groups…' : adgroups.length ? 'Pick an ad group' : 'No Display ad groups yet'}</option>
                  {byCampaign.map((c) => (
                    <optgroup key={c.name} label={c.name}>
                      {c.groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
                    </optgroup>
                  ))}
                </Select>
                {adgroupId && (
                  <div className="mt-3">
                    <div className="mb-1.5 flex items-baseline justify-between">
                      <span className="text-2xs font-semibold uppercase tracking-wide text-text-muted">Who and where</span>
                      {existingAds !== null && <span className="text-2xs text-text-muted">{existingAds} ad{existingAds === 1 ? '' : 's'} already</span>}
                    </div>
                    {loadingGroup ? <span className="flex items-center gap-1.5 text-2xs text-text-muted"><Spinner size={11} /> Loading…</span>
                      : <ChipList items={targetingChips(targeting)} empty="Everyone on the Display Network" max={14} />}
                  </div>
                )}
              </Section>

              {ads.map((a, k) => (
                <RdaDraftCard key={a.id} ad={a} index={k} focused={active?.id === a.id} onFocus={() => setActiveAd(a.id)}
                  onChange={(p) => patchAd(a.id, p)} made={!!madeRef.current[a.id]} state={pub[a.id]} disabled={saving}
                  onDuplicate={() => { const c = cloneRda(a); setAds((as) => { const i = as.findIndex((x) => x.id === a.id); return [...as.slice(0, i + 1), c, ...as.slice(i + 1)]; }); setActiveAd(c.id); }}
                  onRemove={ads.length > 1 ? () => setAds((as) => as.filter((x) => x.id !== a.id)) : undefined} />
              ))}

              <Button variant="secondary" className="w-full" iconLeft={<Plus size={15} />} disabled={saving}
                onClick={() => { const a = newRda(ads.length + 1, brandOf(ads[ads.length - 1]) ?? brand); setAds((as) => [...as, a]); setActiveAd(a.id); }}>
                Add another ad
              </Button>

              <SaveBar issues={issues} error={error} saving={saving} onSave={submit} onCancel={() => navigate(CFG.base)}
                label={ads.length === 1 ? 'Create ad' : `Create ${ads.length} ads`} />
            </div>

            <div className="hidden w-[320px] shrink-0 xl:block">
              <div className="sticky top-24"><RdaPreview ad={active} /></div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
